import React, { useState } from 'react';
import { Play, Copy, Download } from 'lucide-react';

interface SimplePythonBlockProps {
  children: string;
  filename?: string;
  title?: string;
}

export const SimplePythonBlock: React.FC<SimplePythonBlockProps> = ({
  children,
  filename = "script.py",
  title = "Python Code"
}) => {
  const [code] = useState(children.trim());
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const runCode = async () => {
    setIsRunning(true);
    setOutput('Running...');
    
    // Simple simulation - in real implementation this would use Pyodide
    setTimeout(() => {
      setOutput('# Code execution simulated\n# In production, this would run actual Python code\nprint("Hello from Python!")');
      setIsRunning(false);
    }, 1000);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const downloadCode = () => {
    const blob = new Blob([code], { type: 'text/python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="my-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {title}
            </span>
            <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
              Python
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={copyCode}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              title="Copy code"
            >
              <Copy size={16} />
            </button>
            <button
              onClick={downloadCode}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              title="Download code"
            >
              <Download size={16} />
            </button>
            <button
              onClick={runCode}
              disabled={isRunning}
              className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Play size={14} />
              <span>{isRunning ? 'Running...' : 'Run'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Code Display */}
      <div className="relative">
        <pre className="p-4 bg-gray-900 text-gray-100 overflow-x-auto text-sm">
          <code>{code}</code>
        </pre>
      </div>

      {/* Output */}
      {output && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Output:</span>
          </div>
          <pre className="p-4 bg-black text-green-400 text-sm overflow-x-auto max-h-64">
            {output}
          </pre>
        </div>
      )}

      {/* Status */}
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Interactive Python Block (Demo)</span>
          <span>Click "Run" to simulate execution</span>
        </div>
      </div>
    </div>
  );
};